import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { PublicLayout } from "@/components/site/PublicLayout";
import { api, fileUrl } from "@/lib/api";
import { format } from "date-fns";

const HERO_IMG = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1600&q=80";
const COMMUNITY_IMG = "https://images.unsplash.com/photo-1515187029135-18ee286d815b?auto=format&fit=crop&w=1200&q=80";
const DOCS_IMG = "https://images.unsplash.com/photo-1631557777232-a2632ae3c67d?auto=format&fit=crop&w=1200&q=80";

const Icon = ({ name, size = 22, className = "" }) => {
    const C = Icons[name] || Icons.Sparkles;
    return <C size={size} className={className} />;
};

export default function Home() {
    const [hp, setHp] = useState(null);
    const [notices, setNotices] = useState([]);
    const [tenders, setTenders] = useState([]);
    const [recentDocs, setRecentDocs] = useState([]);
    const [events, setEvents] = useState([]);
    const [societies, setSocieties] = useState([]);
    const [contact, setContact] = useState(null);
    const [albums, setAlbums] = useState([]);

    useEffect(() => {
        api.get("/homepage").then((r) => setHp(r.data));
        api.get("/documents", { params: { category: "Notices", limit: 5 } }).then((r) => setNotices(r.data));
        api.get("/documents", { params: { category: "Tender Notices", limit: 5 } }).then((r) => setTenders(r.data));
        api.get("/documents", { params: { limit: 6 } }).then((r) => setRecentDocs(r.data));
        api.get("/events", { params: { scope: "upcoming", limit: 3 } }).then((r) => setEvents(r.data));
        api.get("/societies", { params: { featured: true } }).then((r) => setSocieties(r.data));
        api.get("/contact").then((r) => setContact(r.data));
        api.get("/gallery/albums").then((r) => setAlbums((r.data || []).slice(0, 6)));
    }, []);

    return (
        <PublicLayout>
            {/* HERO */}
            <section className="relative overflow-hidden bg-gradient-to-br from-white via-white to-brand-blue-light" data-testid="home-hero">
                <div className="absolute inset-0 bg-grid opacity-40" />
                <div className="container-x relative grid gap-10 py-14 md:grid-cols-12 md:py-24">
                    <div className="md:col-span-7">
                        <div className="overline" data-testid="hero-overline">Registered Co-Operative Association</div>
                        <h1 className="mt-4 font-serif text-4xl leading-[1.05] text-slate-900 sm:text-5xl lg:text-6xl">
                            {hp?.hero?.title || "Gaurav Sankalp Phase-1"}
                            <span className="block text-brand-blue">{hp?.hero?.subtitle || "Housing Societies Association"}</span>
                        </h1>
                        <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600">
                            {hp?.hero?.tagline || "Working Together for a Better Community"}. Coordinating governance,
                            community welfare and transparent operations across our member societies in Mira Road (East).
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/about" className="btn-primary" data-testid="hero-learn-more">
                                Learn More <Icons.ArrowRight size={16} />
                            </Link>
                            <Link to="/societies" className="btn-outline" data-testid="hero-view-societies">
                                View Our Societies
                            </Link>
                        </div>
                    </div>
                    <div className="md:col-span-5">
                        <div className="relative">
                            <img
                                src={HERO_IMG}
                                alt="Modern residential society"
                                loading="lazy"
                                className="h-[380px] w-full rounded-3xl object-cover shadow-xl sm:h-[440px]"
                            />
                            <div className="absolute -bottom-6 -left-6 hidden w-52 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl sm:block">
                                <div className="overline">Since 2012</div>
                                <div className="mt-1 font-serif text-2xl text-slate-900">14 Years</div>
                                <div className="text-xs text-slate-500">of dedicated community service</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* marquee */}
                {notices.length > 0 && (
                    <div className="border-y border-slate-200 bg-brand-blue-light/60">
                        <div className="container-x flex items-center gap-4 py-3">
                            <span className="shrink-0 rounded-full bg-brand-blue px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">Latest</span>
                            <div className="relative flex-1 overflow-hidden">
                                <div className="flex min-w-max animate-marquee gap-24 whitespace-nowrap text-sm text-brand-blue">
                                    {[...notices, ...notices].map((n, i) => (
                                        <span key={i} className="inline-flex items-center gap-2">
                                            <Icons.Megaphone size={14} className="text-brand-orange" /> {n.title}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {/* WELCOME + CHAIRMAN */}
            <section className="container-x py-20" data-testid="home-welcome">
                <div className="grid gap-12 md:grid-cols-12">
                    <div className="md:col-span-7">
                        <div className="overline">Welcome</div>
                        <h2 className="section-title mt-3">{hp?.welcome?.heading || "Welcome to Our Association"}</h2>
                        <p className="mt-6 text-lg leading-relaxed text-slate-600">{hp?.welcome?.body}</p>
                        <div className="mt-8 grid grid-cols-2 gap-4">
                            <div className="rounded-2xl border border-slate-200 bg-white p-6">
                                <div className="overline text-brand-orange">Vision</div>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">{hp?.vision_mission?.vision}</p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-6">
                                <div className="overline text-brand-orange">Mission</div>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">{hp?.vision_mission?.mission}</p>
                            </div>
                        </div>
                    </div>
                    <div className="md:col-span-5">
                        <div className="surface-card p-7">
                            <div className="flex items-center gap-4">
                                {hp?.chairman?.photo_id ? (
                                    <img src={fileUrl(hp.chairman.photo_id)} alt={hp.chairman.name} className="h-16 w-16 rounded-full object-cover" />
                                ) : (
                                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-blue-light font-serif text-2xl text-brand-blue">
                                        {(hp?.chairman?.name || "C").charAt(0)}
                                    </div>
                                )}
                                <div>
                                    <div className="font-serif text-xl text-slate-900">{hp?.chairman?.name}</div>
                                    <div className="text-sm text-slate-500">{hp?.chairman?.designation}</div>
                                </div>
                            </div>
                            <div className="mt-5 border-l-2 border-brand-orange pl-5">
                                <p className="italic leading-relaxed text-slate-600">"{hp?.chairman?.message}"</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* HIGHLIGHTS */}
            <section className="border-y border-slate-200 bg-slate-50/60 py-20" data-testid="home-highlights">
                <div className="container-x">
                    <div className="mb-10 flex items-end justify-between gap-4">
                        <div>
                            <div className="overline">Why We Exist</div>
                            <h2 className="section-title mt-3">What Makes Us a Community</h2>
                        </div>
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {(hp?.highlights || []).map((h) => (
                            <div key={h.id} className="surface-card-hover p-7" data-testid={`highlight-${h.title}`}>
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-blue-light text-brand-blue">
                                    <Icon name={h.icon} size={22} />
                                </div>
                                <h3 className="mt-5 font-serif text-2xl text-slate-900">{h.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">{h.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* NOTICES + TENDERS + DOCS */}
            <section className="container-x py-20">
                <div className="grid gap-10 md:grid-cols-3">
                    <FeedCard title="Latest Notices" testid="latest-notices" items={notices} to="/documents?category=Notices" icon="Megaphone" />
                    <FeedCard title="Tender Notices" testid="latest-tenders" items={tenders} to="/documents?category=Tender%20Notices" icon="Gavel" />
                    <FeedCard title="Recent Documents" testid="recent-docs" items={recentDocs} to="/documents" icon="FileText" />
                </div>
            </section>

            {/* EVENTS + GALLERY */}
            <section className="border-t border-slate-200 bg-slate-50/60 py-20" data-testid="home-events">
                <div className="container-x grid gap-12 md:grid-cols-12">
                    <div className="md:col-span-7">
                        <div className="overline">Upcoming</div>
                        <h2 className="section-title mt-3">Events on the Horizon</h2>
                        <div className="mt-8 space-y-4">
                            {events.length === 0 && <p className="text-slate-500">No upcoming events scheduled yet.</p>}
                            {events.map((e) => (
                                <div key={e.id} className="surface-card flex items-start gap-5 p-5" data-testid={`event-card-${e.id}`}>
                                    <div className="grid h-16 w-16 shrink-0 place-items-center rounded-xl bg-brand-blue text-white">
                                        <div className="text-center">
                                            <div className="text-[10px] uppercase tracking-wider opacity-80">{e.date ? format(new Date(e.date), "MMM") : ""}</div>
                                            <div className="text-xl font-semibold">{e.date ? format(new Date(e.date), "dd") : ""}</div>
                                        </div>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-serif text-lg text-slate-900">{e.title}</div>
                                        <div className="text-xs text-slate-500">{e.venue}</div>
                                        <p className="mt-1 line-clamp-2 text-sm text-slate-600">{e.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Link to="/events" className="btn-outline mt-6">All events <Icons.ArrowRight size={14} /></Link>
                    </div>

                    <div className="md:col-span-5">
                        <div className="overline">Gallery</div>
                        <h2 className="section-title mt-3">Life in Our Community</h2>
                        <div className="mt-8 grid grid-cols-2 gap-3">
                            {[COMMUNITY_IMG, HERO_IMG, DOCS_IMG, COMMUNITY_IMG].map((src, i) => (
                                <img key={i} src={src} alt="" loading="lazy" className="h-32 w-full rounded-xl object-cover shadow-sm sm:h-36" />
                            ))}
                        </div>
                        <Link to="/gallery" className="btn-outline mt-6">Explore gallery <Icons.ArrowRight size={14} /></Link>
                    </div>
                </div>
            </section>

            {/* FEATURED SOCIETIES */}
            <section className="container-x py-20" data-testid="home-featured-societies">
                <div className="mb-10 flex items-end justify-between">
                    <div>
                        <div className="overline">Member Societies</div>
                        <h2 className="section-title mt-3">Featured Societies</h2>
                    </div>
                    <Link to="/societies" className="btn-ghost">View all <Icons.ArrowRight size={14} /></Link>
                </div>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                    {societies.slice(0, 4).map((s) => (
                        <Link key={s.id} to={`/societies/${s.slug}`} className="surface-card-hover overflow-hidden" data-testid={`featured-society-${s.slug}`}>
                            <div className="relative h-40 bg-gradient-to-br from-brand-blue to-brand-blue-dark">
                                {s.cover_id ? (
                                    <img src={fileUrl(s.cover_id)} alt={s.name} className="h-full w-full object-cover" loading="lazy" />
                                ) : (
                                    <div className="grid h-full place-items-center text-white/60"><Icons.Building2 size={48} /></div>
                                )}
                            </div>
                            <div className="p-5">
                                <div className="font-serif text-lg text-slate-900 line-clamp-1">{s.name}</div>
                                <div className="text-xs text-slate-500">Reg: {s.registration_number || "—"}</div>
                                <p className="mt-2 line-clamp-2 text-sm text-slate-600">{s.short_description}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* CONTACT + MAP */}
            <section className="border-t border-slate-200 bg-slate-50/60 py-20" data-testid="home-contact">
                <div className="container-x grid gap-10 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <div className="overline">Reach Us</div>
                        <h2 className="section-title mt-3">Come Say Hello</h2>
                        <ul className="mt-6 space-y-4 text-slate-600">
                            <li className="flex gap-3"><Icons.MapPin size={18} className="mt-1 text-brand-orange" /><span>{contact?.address}</span></li>
                            <li className="flex gap-3"><Icons.Mail size={18} className="mt-1 text-brand-orange" /><a href={`mailto:${contact?.email || ""}`} className="hover:text-brand-blue">{contact?.email}</a></li>
                            {contact?.phone && <li className="flex gap-3"><Icons.Phone size={18} className="mt-1 text-brand-orange" />{contact.phone}</li>}
                            {contact?.office_timing && <li className="flex gap-3"><Icons.Clock size={18} className="mt-1 text-brand-orange" />{contact.office_timing}</li>}
                        </ul>
                        <Link to="/contact" className="btn-accent mt-6">Contact Us <Icons.ArrowRight size={14} /></Link>
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm md:col-span-7">
                        {contact?.map_embed_url && (
                            <iframe
                                title="Location Map"
                                src={contact.map_embed_url}
                                className="h-[380px] w-full"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        )}
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}

const FeedCard = ({ title, items, to, icon, testid }) => {
    const Ic = Icons[icon] || Icons.FileText;
    return (
        <div className="surface-card p-6" data-testid={testid}>
            <div className="flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-brand-blue-light text-brand-blue"><Ic size={18} /></div>
                <div className="font-serif text-xl text-slate-900">{title}</div>
            </div>
            <ul className="mt-5 divide-y divide-slate-100">
                {items.slice(0, 5).map((it) => (
                    <li key={it.id} className="flex items-start gap-3 py-3 text-sm">
                        {it.pinned ? <Icons.Pin size={14} className="mt-0.5 text-brand-orange" /> : <Icons.ChevronRight size={14} className="mt-0.5 text-slate-400" />}
                        <div className="min-w-0 flex-1">
                            <div className="line-clamp-2 text-slate-700">{it.title}</div>
                            <div className="text-[11px] text-slate-400">{it.created_at ? format(new Date(it.created_at), "dd MMM yyyy") : ""}</div>
                        </div>
                    </li>
                ))}
                {items.length === 0 && <li className="py-3 text-sm text-slate-400">Nothing to show yet.</li>}
            </ul>
            <Link to={to} className="btn-ghost mt-3 !px-0 text-brand-blue">View all →</Link>
        </div>
    );
};
